import type { IOption, SelectOptionValue } from '../types';

export const NO_HIGHLIGHTED_OPTION_INDEX = -1;
export const NEXT_OPTION_OFFSET = 1;
export const PREVIOUS_OPTION_OFFSET = -NEXT_OPTION_OFFSET;

interface IGetResolvedHighlightedIndexParams<TValue extends SelectOptionValue, TMetadata> {
	deferInitialHighlight?: boolean;
	highlightedIndex: number;
	initialHighlightedIndex: number;
	isOpen: boolean;
	options: ReadonlyArray<IOption<TValue, TMetadata>>;
	optionsChanged: boolean;
	pendingHighlightedIndexOnOpen?: number;
	preserveDeferredHighlightOnOptionsChange?: boolean;
	preferredHighlightedIndexOnValueChange?: number;
	previousHighlightedIndex: number;
	previousHighlightedValue?: TValue;
	valueChanged: boolean;
}

/**
 * Returns the indexes of options available for keyboard interaction.
 */
export const getEnabledOptionIndexes = <TValue extends SelectOptionValue, TMetadata>(
	options: ReadonlyArray<IOption<TValue, TMetadata>>
): number[] =>
	options.reduce<number[]>((indexes, option, index) => {
		if (!option.disabled) {
			indexes.push(index);
		}
		return indexes;
	}, []);

interface IGetInitialHighlightedIndexParams<TValue extends SelectOptionValue, TMetadata> {
	enabledOptionIndexes: ReadonlyArray<number>;
	options: ReadonlyArray<IOption<TValue, TMetadata>>;
	value: ReadonlyArray<TValue>;
}

/**
 * Finds the selected enabled option or falls back to the first enabled option.
 */
export const getInitialHighlightedIndex = <TValue extends SelectOptionValue, TMetadata>({
	enabledOptionIndexes,
	options,
	value,
}: IGetInitialHighlightedIndexParams<TValue, TMetadata>): number => {
	const selectedOptionIndex = options.findIndex(option => !option.disabled && value.includes(option.value));
	return selectedOptionIndex === NO_HIGHLIGHTED_OPTION_INDEX
		? enabledOptionIndexes[0] ?? NO_HIGHLIGHTED_OPTION_INDEX
		: selectedOptionIndex;
};

/**
 * Keeps the rendered highlighted option valid for the current option list.
 */
export const getResolvedHighlightedIndex = <TValue extends SelectOptionValue, TMetadata>({
	deferInitialHighlight = false,
	highlightedIndex,
	initialHighlightedIndex,
	isOpen,
	options,
	optionsChanged,
	pendingHighlightedIndexOnOpen,
	preserveDeferredHighlightOnOptionsChange = false,
	preferredHighlightedIndexOnValueChange,
	previousHighlightedIndex,
	previousHighlightedValue,
	valueChanged,
}: IGetResolvedHighlightedIndexParams<TValue, TMetadata>): number => {
	if (!isOpen) {
		return NO_HIGHLIGHTED_OPTION_INDEX;
	}

	if (
		deferInitialHighlight &&
		highlightedIndex === NO_HIGHLIGHTED_OPTION_INDEX &&
		(!optionsChanged || preserveDeferredHighlightOnOptionsChange) &&
		!valueChanged
	) {
		return NO_HIGHLIGHTED_OPTION_INDEX;
	}

	if (pendingHighlightedIndexOnOpen !== undefined) {
		if (pendingHighlightedIndexOnOpen === NO_HIGHLIGHTED_OPTION_INDEX) {
			return NO_HIGHLIGHTED_OPTION_INDEX;
		}
		const pendingHighlightedOption = options[pendingHighlightedIndexOnOpen];
		return pendingHighlightedOption && !pendingHighlightedOption.disabled
			? pendingHighlightedIndexOnOpen
			: initialHighlightedIndex;
	}

	if (valueChanged) {
		if (preferredHighlightedIndexOnValueChange === undefined) {
			return initialHighlightedIndex;
		}
		const preferredHighlightedOption = options[preferredHighlightedIndexOnValueChange];
		return preferredHighlightedOption && !preferredHighlightedOption.disabled
			? preferredHighlightedIndexOnValueChange
			: initialHighlightedIndex;
	}

	if (
		optionsChanged &&
		highlightedIndex === previousHighlightedIndex &&
		previousHighlightedValue !== undefined
	) {
		const preservedHighlightedIndex = options.findIndex(
			option => !option.disabled && option.value === previousHighlightedValue
		);

		return preservedHighlightedIndex === NO_HIGHLIGHTED_OPTION_INDEX
			? initialHighlightedIndex
			: preservedHighlightedIndex;
	}

	const highlightedOption = options[highlightedIndex];
	return highlightedOption && !highlightedOption.disabled ? highlightedIndex : initialHighlightedIndex;
};

interface IGetNextHighlightedIndexParams {
	enabledOptionIndexes: ReadonlyArray<number>;
	highlightedIndex: number;
	offset: number;
}

/**
 * Moves to the next enabled option and wraps at either boundary.
 */
export const getNextHighlightedIndex = ({
	enabledOptionIndexes,
	highlightedIndex,
	offset,
}: IGetNextHighlightedIndexParams): number => {
	if (enabledOptionIndexes.length === 0) {
		return NO_HIGHLIGHTED_OPTION_INDEX;
	}

	const currentPosition = enabledOptionIndexes.indexOf(highlightedIndex);
	const initialPosition = offset === NEXT_OPTION_OFFSET ? 0 : enabledOptionIndexes.length - 1;
	const nextPosition =
		currentPosition === NO_HIGHLIGHTED_OPTION_INDEX
			? initialPosition
			: (currentPosition + offset + enabledOptionIndexes.length) % enabledOptionIndexes.length;
	return enabledOptionIndexes[nextPosition];
};
